import { Descendant } from "slate";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import {
  EMAIL_DYNAMIC_VARIABLE_REGEX,
  parseEmailDynamicVariableMatch,
} from "@mapmycustomers/shared/util/email/parseEmailDynamicVariables";
import { splitStringByRegExp } from "@mapmycustomers/shared/util/stringUtils";

import { MmcText } from "../../../type/slate-types";
import ElementType from "../../../type/slate/ElementType";
import TextSize from "../../../type/slate/TextSize";

import { TEXT_SIZE_TO_FONT_SIZE } from "./const";

type MmcTextStyles = Omit<MmcText, "text">;

export const isTextNode = (element: HTMLElement): boolean => {
  return ["b", "br", "em", "span", "u"].includes(element.tagName.toLowerCase());
};

export const parseTextChildNode = (node: ChildNode, parentStyles?: MmcTextStyles): Descendant[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || "";
    const parts = splitStringByRegExp(text, EMAIL_DYNAMIC_VARIABLE_REGEX);
    if (parts.length === 0) {
      return [{ ...parentStyles, text: "" }];
    }
    return parts.map((part) => {
      if (part.type === "text") {
        return { ...parentStyles, text: part.text };
      }
      const { name, prefix } = parseEmailDynamicVariableMatch(part.match);
      return {
        children: [{ text: "" }],
        name,
        prefix,
        type: ElementType.DYNAMIC_VARIABLE,
        value: part.match[0],
      };
    });
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    return parseTextNode(node as HTMLElement, parentStyles);
  }

  return [];
};

const parseTextNode = (element: HTMLElement, parentStyles?: MmcTextStyles): Descendant[] => {
  const childParentStyles: MmcTextStyles = { ...parentStyles };

  switch (element.tagName.toLowerCase()) {
    case "br": {
      // if it's line break - just return simple text
      return [{ text: "" }];
    }
    case "b": {
      childParentStyles.bold = true;
      break;
    }
    case "em": {
      childParentStyles.italic = true;
      break;
    }
    case "u": {
      childParentStyles.underline = true;
      break;
    }
  }

  if (element.style.color) {
    childParentStyles.color = element.style.color as AnyColor;
  }

  const fontSize = element.style.fontSize;
  if (fontSize) {
    childParentStyles.size =
      fontSize === `${TEXT_SIZE_TO_FONT_SIZE[TextSize.LARGE]}px`
        ? TextSize.LARGE
        : fontSize === `${TEXT_SIZE_TO_FONT_SIZE[TextSize.SMALL]}px`
          ? TextSize.SMALL
          : TextSize.MEDIUM;
  }

  const textDecoration = element.style.textDecoration;
  if (textDecoration === "line-through") {
    childParentStyles.strike = true;
  }

  return Array.from(element.childNodes)
    .map((childNode) => parseTextChildNode(childNode, childParentStyles))
    .flat();
};

export default parseTextNode;
